import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Snackbar,
  Tab,
  Tabs,
  Typography,
  Card,
  CardContent,
  Skeleton,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import Cookies from "js-cookie";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DeleteCanidateDrawer from "../components/deleteCandidateDrawer";
import PauseResumeCanidateDrawer from "../components/pauseResumeCandidateDrawer";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import PersonIcon from "@mui/icons-material/Person";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import { useSelector } from "react-redux";
import UserAdditon from "./userAdditon";

// ── Shared styles ─────────────────────────────────────────────────────────────
const cardHeaderStyle = {
  borderRadius: "4px 4px 0 0",
  padding: "14px 20px",
  backgroundColor: "rgba(63, 149, 234, 0.20)",
  color: "#000",
  fontSize: "14px",
  fontWeight: "600",
  flexShrink: 0,
};
const labelStyle = {
  fontWeight: 400,
  fontSize: "14px",
  color: "rgba(0,0,0,0.60)",
  width: "150px",
  whiteSpace: "normal",
  wordBreak: "break-word",
};
const valueStyle = {
  fontWeight: 400,
  fontSize: "14px",
  color: "rgba(0,0,0,0.87)",
  whiteSpace: "normal",
  wordBreak: "break-word",
  flex: 1,
};
const chipStyle = {
  fontSize: "13px",
  padding: "8px 16px",
  backgroundColor: "#E3F2FD",
  borderRadius: "8px",
  color: "#000",
  fontWeight: 400,
};
const emptyStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "80px",
};
const cardBaseStyle = {
  border: "1px solid rgba(115,119,145,0.08)",
  borderRadius: "4px",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

// ── Shimmer: Card 1 ───────────────────────────────────────────────────────────
const BasicInfoShimmer = () => (
  <Card elevation={0} sx={cardBaseStyle}>
    <Box sx={cardHeaderStyle}>
      <Skeleton width={200} height={20} sx={{ bgcolor: "rgba(0,0,0,0.08)" }} />
    </Box>
    <CardContent sx={{ padding: "16px 20px", flexGrow: 1 }}>
      {[1, 2, 3, 4].map((i) => (
        <Box key={i} sx={{ mb: "14px", display: "flex", gap: "20px" }}>
          <Skeleton width={100} height={18} sx={{ flexShrink: 0 }} />
          <Skeleton width={160} height={18} />
        </Box>
      ))}
      <Box sx={{ borderTop: "1px dashed rgba(0,0,0,0.10)", my: "14px" }} />
      <Skeleton width={110} height={18} sx={{ mb: "14px" }} />
      {[1, 2].map((i) => (
        <Box key={i} sx={{ mb: "10px", display: "flex", gap: "20px" }}>
          <Skeleton width={100} height={18} sx={{ flexShrink: 0 }} />
          <Skeleton width={140} height={18} />
        </Box>
      ))}
    </CardContent>
  </Card>
);

// ── Shimmer: Card 2 (Companies) ───────────────────────────────────────────────
const CompaniesShimmer = () => (
  <Card elevation={0} sx={cardBaseStyle}>
    <Box sx={cardHeaderStyle}>
      <Skeleton width={100} height={20} sx={{ bgcolor: "rgba(0,0,0,0.08)" }} />
    </Box>
    <CardContent sx={{ padding: "16px 20px", flexGrow: 1 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
        {[120, 90, 140, 100, 130, 80, 110, 95, 125, 85].map((w, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            width={w}
            height={36}
            sx={{ borderRadius: "8px" }}
          />
        ))}
      </Box>
    </CardContent>
  </Card>
);

// ── Shimmer: Card 3 (Branches) ────────────────────────────────────────────────
const BranchesShimmer = () => (
  <Card elevation={0} sx={cardBaseStyle}>
    <Box sx={cardHeaderStyle}>
      <Skeleton width={90} height={20} sx={{ bgcolor: "rgba(0,0,0,0.08)" }} />
    </Box>
    <CardContent sx={{ padding: "16px 20px", flexGrow: 1 }}>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Box
          key={i}
          sx={{
            mb: "14px",
            display: "flex",
            alignItems: "center",
            gap: "24px",
          }}
        >
          <Skeleton width={110} height={18} sx={{ flexShrink: 0 }} />
          <Skeleton
            variant="rounded"
            width={100}
            height={34}
            sx={{ borderRadius: "8px" }}
          />
        </Box>
      ))}
    </CardContent>
  </Card>
);

// ── InfoRow ───────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value, capitalize = true }) => (
  <Box sx={{ mb: "10px", display: "flex", gap: "20px" }}>
    <Typography sx={labelStyle}>{label}</Typography>
    <Typography
      sx={{ ...valueStyle, textTransform: capitalize ? "capitalize" : "none" }}
    >
      {value || "NA"}
    </Typography>
  </Box>
);

// ── Component ─────────────────────────────────────────────────────────────────
const UserDetails = () => {
  const navigate = useNavigate();
  const [backbutton, setBackbutton] = useState(false);
  const [basicInfo, setBasicInfo] = useState({});
  const [displayCompanyInfo, setDisplayCompanyInfo] = useState([]);
  const [displayBranchInfo, setDisplayBranchInfo] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);

  const { id } = useParams();

  const [open, setOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [underAssignRole, setUnderAssignRole] = useState();
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [editUserOpen, setEditUserOpen] = useState(false);

  const managePermissions = useSelector((state) => state.user.permissions);
  const PauseResumeUserPermissions = managePermissions["600"];

  // ── API — same as original ─────────────────────────────────────────────────
  const getUserInfoDetails = async () => {
    setPageLoading(true);
    try {
      const response = await axios.get(`/user-info/${id}`, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      setBasicInfo(response.data.message.basic_info);
      setDisplayCompanyInfo(response.data.message.companiesList || []);
      setDisplayBranchInfo(response.data.message.companiesBranchList || []);
    } catch (error) {
      alert("Page is not found");
      navigate("/Users?page=1");
    } finally {
      setPageLoading(false);
    }
  };

  const fetchChildAuthority = async () => {
    try {
      const response = await axios.post(
        "/child-users",
        { user_role_id: basicInfo?.role_id, user_id: id },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      if (response.data.status === 200) setRoleList(response.data.message);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getUserInfoDetails();
  }, []);

  useEffect(() => {
    if (open) fetchChildAuthority();
  }, [open]);

  // ── Handlers — same as original ────────────────────────────────────────────
  const handleBack = () => {
    const previousUrl = sessionStorage.getItem("UserpreviousUrl");
    if (previousUrl) {
      const url = new URL(previousUrl, window.location.origin);
      navigate(`${url.pathname}${url.search}`);
      sessionStorage.removeItem("UserpreviousUrl");
    } else {
      navigate("/Users?page=1");
    }
  };

  const handleAcknowledgeChange = (e) => setAcknowledged(e.target.checked);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setAcknowledged(false);
    setUnderAssignRole();
  };
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setAcknowledged(false);
  };

  const handleunderAssignRole = (event) => {
    const selectedRoleId = event.target.value;
    const selectedRole = roleList.find((r) => r.id === selectedRoleId);
    setUnderAssignRole(selectedRole);
  };

  const handleDelete = async () => {
    if (!(acknowledged && underAssignRole?.id)) return;
    try {
      await axios.post(
        `/delete-user/${id}`,
        {
          under_assigned_user_id: underAssignRole?.id,
          role_id: basicInfo?.role_id,
        },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      setSnackbarMessage("User Deleted Successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => navigate("/Users?page=1"), 800);
    } catch (err) {
      setSnackbarMessage("User Deletion Failed");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setOpen(false);
      setAcknowledged(false);
    }
  };

  const handlePauseResume = async () => {
    const my_user_id = localStorage.getItem("user_id");
    if (!acknowledged) return;
    try {
      setOpenDialog(false);
      setAcknowledged(false);
      const response = await axios.post(
        "/pause-resume-user",
        {
          is_pause: basicInfo?.is_pause ? 0 : 1,
          user_id: basicInfo?.user_id,
          my_user_id,
        },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      if (response.data.status == 200) {
        setSnackbarMessage(
          `User ${basicInfo?.is_pause ? "resumed" : "paused"} successfully`,
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        getUserInfoDetails();
      } else {
        setSnackbarMessage(
          `Error in ${basicInfo?.is_pause ? "pausing" : "resuming"} user`,
        );
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage("Operation Failed");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setOpen(false);
      setAcknowledged(false);
    }
  };

  const { firstName, lastName, email } = basicInfo;

  // ── Cards — same as original ───────────────────────────────────────────────
  const BasicInfoRoleCard = () => (
    <Card elevation={0} sx={cardBaseStyle}>
      <Box sx={cardHeaderStyle}>Basic Info </Box>
      <CardContent
        sx={{ padding: "16px 20px", overflowY: "auto", flexGrow: 1 }}
      >
        <InfoRow label="First Name" value={firstName} />
        <InfoRow label="Last Name" value={lastName} />
        <InfoRow label="Email" value={email} capitalize={false} />
        <InfoRow label="Role" value={basicInfo?.role_name} />
        <Box sx={{ borderTop: "1px dashed rgba(0,0,0,0.10)", my: "14px" }} />
        <Typography
          sx={{ fontSize: "13px", fontWeight: 600, color: "#000", mb: "10px" }}
        >
          Role Permissions
        </Typography>
        <InfoRow label="Role Type" value={basicInfo?.role_name} />
        <InfoRow
          label="Assigned Under"
          value={basicInfo?.under_assigned_user_name}
        />
      </CardContent>
    </Card>
  );

  const CompaniesCard = () => (
    <Card elevation={0} sx={cardBaseStyle}>
      <Box sx={cardHeaderStyle}>Companies</Box>
      <CardContent
        sx={{ padding: "16px 20px", overflowY: "auto", flexGrow: 1 }}
      >
        {displayCompanyInfo?.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {displayCompanyInfo.map((company, idx) => (
              <Box key={idx} sx={chipStyle}>
                {company?.company_name || company?.name || "NA"}
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={emptyStyle}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "14px",
                color: "text.secondary",
              }}
            >
              No companies available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const BranchesCard = () => (
    <Card elevation={0} sx={cardBaseStyle}>
      <Box sx={cardHeaderStyle}>Branches</Box>
      <CardContent
        sx={{ padding: "16px 20px", overflowY: "auto", flexGrow: 1 }}
      >
        {displayCompanyInfo?.length > 0 ? (
          displayCompanyInfo.map((company, idx) => {
            const branches = displayBranchInfo?.filter(
              (b) => b.company_id === company.company_id,
            );
            return (
              <Box
                key={idx}
                sx={{
                  mb: "14px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "24px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "rgba(0,0,0,0.87)",
                    minWidth: "120px",
                    pt: "6px",
                  }}
                >
                  {company?.company_name || company?.name}
                </Typography>
                <Box>
                  {branches?.length > 0 ? (
                    <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {branches.map((branch, i) => (
                        <Box key={i} sx={chipStyle}>
                          {branch?.branch_name}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={chipStyle}>No Branches</Box>
                  )}
                </Box>
              </Box>
            );
          })
        ) : (
          <Box sx={emptyStyle}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "14px",
                color: "text.secondary",
              }}
            >
              No branches available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box className="pageWrapper">
      {/* ══ HEADER ══ */}
      <Box sx={{ backgroundColor: "#fff", borderRadius: "12px", pb: 0, mb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            onClick={handleBack}
            onMouseEnter={() => setBackbutton(true)}
            onMouseLeave={() => setBackbutton(false)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              pl: "6px",
              pr: backbutton ? "10px" : "6px",
              height: "26px",
              border: `1px solid ${backbutton ? "#3F95EA" : "#E2E8F0"}`,
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: backbutton ? "#3F95EA" : "#fff",
              transition: "all 0.2s ease",
              overflow: "hidden",
            }}
          >
            <ChevronLeftIcon
              sx={{
                fontSize: "18px",
                color: backbutton ? "#fff" : "#64748B",
                transition: "color 0.2s",
              }}
            />
            {backbutton && (
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                Back
              </Typography>
            )}
          </Box>
          <Typography
            sx={{ fontSize: "18px", fontWeight: 700, color: "#151D48" }}
          >
            User Details
          </Typography>
        </Box>

        <Box sx={{ height: "1px", background: "rgba(21,29,72,0.12)", mb: 2 }} />

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Left: avatar + name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {pageLoading ? (
              <Skeleton
                variant="rounded"
                width={64}
                height={64}
                sx={{ borderRadius: "10px", flexShrink: 0 }}
              />
            ) : (
              <Box
                sx={{
                  width: "64px",
                  height: "64px",
                  flexShrink: 0,
                  border: "1px solid #E2E8F0",
                  borderRadius: "10px",
                  backgroundColor: "#F8FAFC",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PersonIcon sx={{ fontSize: "48px", color: "#CBD5E1" }} />
              </Box>
            )}
            <Box>
              {pageLoading ? (
                <>
                  <Skeleton width={180} height={28} sx={{ mb: "4px" }} />
                  <Skeleton width={100} height={20} />
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: 600,
                      color: "rgba(0,0,0,0.87)",
                      textTransform: "capitalize",
                    }}
                  >
                    {firstName || "NA"} {lastName || ""}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "14px", color: "rgba(0,0,0,0.60)" }}
                  >
                    {basicInfo?.role_name || "NA"}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {/* Right: action buttons — CompanyDetails jaisi exact styling ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            {pageLoading ? (
              <>
                <Skeleton
                  variant="rounded"
                  width={64}
                  height={34}
                  sx={{ borderRadius: "8px" }}
                />
                <Skeleton
                  variant="rounded"
                  width={82}
                  height={34}
                  sx={{ borderRadius: "8px" }}
                />
                <Skeleton
                  variant="rounded"
                  width={34}
                  height={34}
                  sx={{ borderRadius: "8px" }}
                />
              </>
            ) : (
              <>
                {/* Edit button — CompanyDetails jaisa: outlined, white bg, blue border+text */}
                <Box
                  onClick={() => setEditUserOpen(true)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    px: "10px",
                    height: "34px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#F8FAFC" },
                  }}
                >
                  <img src="/assets/edit.svg" alt="" />
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 500, color: "#3F95EA" }}
                  >
                    Edit
                  </Typography>
                </Box>

                {/* Pause/Resume button — same outlined style */}
                {PauseResumeUserPermissions?.pauseResume == 1 && (
                  <Box
                    onClick={handleOpenDialog}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      px: "10px",
                      height: "34px",
                      border: `1px solid ${basicInfo?.is_pause ? "#ddf7e9" : "#f5eae3"}`,
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: "#fff",
                      "&:hover": {
                        backgroundColor: `${basicInfo?.is_pause ? "#ecfff5" : "#fdf9f6"} `,
                      },
                    }}
                  >
                    {basicInfo?.is_pause ? (
                      <PlayCircleFilledWhiteIcon
                        sx={{ fontSize: "16px", color: "#38A169" }}
                      />
                    ) : (
                      <PauseCircleIcon
                        sx={{ fontSize: "16px", color: "#DD6B20" }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: basicInfo?.is_pause ? "#38A169" : "#DD6B20",
                      }}
                    >
                      {basicInfo?.is_pause ? "Resume" : "Pause"}
                    </Typography>
                  </Box>
                )}

                {/* Delete button — CompanyDetails jaisa: white bg, red border, red icon */}
                <Box
                  onClick={handleOpen}
                  sx={{
                    width: "34px",
                    height: "34px",
                    border: "1px solid #FECACA",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#FFF5F5" },
                  }}
                >
                  <img src="/assets/delete.svg" />
                </Box>
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 2, borderBottom: "1px solid rgba(21,29,72,0.12)" }}>
          <Tabs
            value={0}
            sx={{
              "& .MuiTabs-indicator": { height: "2px", borderRadius: "2px" },
              "& .MuiTab-root": {
                fontSize: "13px",
                fontWeight: 500,
                letterSpacing: "1.25px",
                minHeight: "40px",
                padding: "6px 12px",
              },
              minHeight: "40px",
            }}
          >
            <Tab label="User Info" />
          </Tabs>
        </Box>
      </Box>

      {/* ══ THREE CARDS ══ */}
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          width: "100%",
          alignItems: "stretch",
          height: "100%",
          overflowY: "hidden",
        }}
      >
        {pageLoading ? (
          <>
            <BasicInfoShimmer />
            <CompaniesShimmer />
            <BranchesShimmer />
          </>
        ) : (
          <>
            <BasicInfoRoleCard />
            <CompaniesCard />
            <BranchesCard />
          </>
        )}
      </Box>

      {/* ══ Drawers & Snackbar — same as original ══ */}
      <DeleteCanidateDrawer
        open={open}
        roleList={roleList}
        handleunderAssignRole={handleunderAssignRole}
        underAssignRole={underAssignRole}
        usersection={true}
        basicInfo={basicInfo}
        handleClose={handleClose}
        acknowledged={acknowledged}
        handleDelete={handleDelete}
        handleAcknowledgeChange={handleAcknowledgeChange}
      />
      <PauseResumeCanidateDrawer
        open={openDialog}
        basicInfo={basicInfo}
        handleClose={handleCloseDialog}
        handleDelete={handlePauseResume}
        acknowledged={acknowledged}
        handleAcknowledgeChange={handleAcknowledgeChange}
      />
      {editUserOpen && (
        <UserAdditon
          open={editUserOpen}
          onClose={() => setEditUserOpen(false)}
          onSuccess={getUserInfoDetails}
          userId={id}
          disableNavigation
        />
      )}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDetails;
